import { ArrowDownIcon, ArrowTopRightOnSquareIcon, ArrowUpIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  useGetProjectsProjectId,
  useGetReferenceMatch,
  useListReferenceMatchDocumentRecords,
} from '@/entities/project/api/generated/api';
import { ProjectReferenceMatchDetailResponseStatus } from '@/entities/project/api/generated/model/projectReferenceMatchDetailResponseStatus';
import { ReferenceChatType } from '@/entities/project/api/generated/model/referenceChatType';
import { CATEGORY_TO_REFERENCE_CHAT_TYPE } from '@/features/ai-matching-results/lib/categoryToReferenceChatType';
import { toMatchedProject, toMatchingCandidate } from '@/features/ai-matching-results/lib/mappers';
import { useReferenceMatchJudgementMutation } from '@/features/ai-matching-results/lib/useReferenceMatchJudgementMutation';
import { MatchingSkillCard } from '@/features/ai-matching-results/ui/matching-skill-card/MatchingSkillCard';
import { useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import { Accordion, Banner, Button, LabeledText, Skeleton, Text } from '@/shared/ui';
import { SideMenuHeader } from '@/widgets/right-side-menu/ui/SideMenuHeader';
import { ProjectTagsManagementSection } from './ProjectTagsManagementSection';

/** getReferenceMatch のステータス → MatchingSkillCard の表示ステータスへの変換 */
const toCardViewStatus = (
  status: ProjectReferenceMatchDetailResponseStatus | undefined,
  isLoading: boolean,
) => {
  if (isLoading) return 'loading' as const;
  switch (status) {
    case ProjectReferenceMatchDetailResponseStatus.completed:
      return 'completed' as const;
    case ProjectReferenceMatchDetailResponseStatus.pending:
    case ProjectReferenceMatchDetailResponseStatus.generating:
      return 'loading' as const;
    case ProjectReferenceMatchDetailResponseStatus.failed:
      return 'failed-server' as const;
    default:
      return 'not-executed' as const;
  }
};

/**
 * AIマッチング結果ページの右サイドパネル（事業情報）
 *
 * 財源詳細（財源別事業一覧）ページのテーブルで事業名をクリックしたときに開く。マッチング候補は事業参照マッチ取得API
 * （getReferenceMatch）から取得し、判定変更は同 API（is_user_selected）へ永続化する。事業の
 * 基本情報は一覧取得（listReferenceMatchDocumentRecords）のクエリキャッシュから引く。
 * 「予算・内容」「管理（タグ）」は一覧 API に含まれないため事業詳細取得 API から別途取得する。
 *
 * SideMenuHeader の上下矢印で、テーブルに表示中（年度・絞り込み適用済み）の事業を前後に遷移できる。
 * 外部リンクボタンで事業詳細ページへ遷移する。
 */
export const AIMatchingProjectDetailSidePanel = () => {
  const { t } = useTranslation();
  const mode = useRouteSidePanelStore((state) => state.mode);
  const setOpen = useRouteSidePanelStore((state) => state.setOpen);
  const requestClose = useRouteSidePanelStore((state) => state.requestClose);

  const category = mode?.type === 'ai-matching-project-detail' ? mode.category : undefined;
  const referenceChatType = category ? CATEGORY_TO_REFERENCE_CHAT_TYPE[category] : undefined;
  // enabled=false 時は呼ばれないため、未確定時はダミーの種別を渡す（型を満たすためのプレースホルダ）。
  // useGetReferenceMatch / useReferenceMatchJudgementMutation の双方で同じ値を使い、片方だけ
  // 取り残されるのを防ぐ。
  const resolvedReferenceChatType =
    referenceChatType ?? ReferenceChatType.prefectural_special_allocation_taxes;
  const year = mode?.type === 'ai-matching-project-detail' ? mode.year : undefined;

  // 一覧と同一パラメータ（reference_chat_type / year / page_size）で取得し、一覧クエリと
  // キャッシュを共有する。year を欠くと別キーになり別年度データを取得してしまう。
  const { data: listData } = useListReferenceMatchDocumentRecords(
    referenceChatType
      ? { reference_chat_type: [referenceChatType], year, page_size: 0 }
      : undefined,
    { query: { enabled: !!referenceChatType } },
  );

  const projectId = mode?.type === 'ai-matching-project-detail' ? mode.projectId : '';
  const { data: matchData, isLoading: isMatchLoading } = useGetReferenceMatch(
    projectId,
    resolvedReferenceChatType,
    { query: { enabled: !!(projectId && referenceChatType) } },
  );

  // 予算・内容・タグは一覧 API に無いため事業詳細を別途取得する。
  const { data: detail, isLoading: isDetailLoading } = useGetProjectsProjectId(projectId, {
    query: { enabled: !!projectId },
  });

  // 判定（is_user_selected）の永続化。事業詳細マッチングタブと同じフックを使う。
  const { updateJudgement } = useReferenceMatchJudgementMutation({
    projectId,
    referenceChatType: resolvedReferenceChatType,
  });

  if (mode?.type !== 'ai-matching-project-detail') {
    return null;
  }
  const { fundingSourceId, documentId, projectIds } = mode;

  const record = listData?.items.find(
    (item) => item.record_id === fundingSourceId && item.document_id === documentId,
  );
  const apiProject = record?.projects.find((p) => p.id === projectId);

  if (!record || !apiProject) {
    return null;
  }

  const project = toMatchedProject(apiProject);

  // カテゴリ → AI スキルカードのタイトル / 説明（i18n の型推論を効かせるため、各 case で
  // リテラルキーを直接 t() に渡す）
  const skillTitle = (() => {
    switch (mode.category) {
      case 'special-allocation-tax-survey':
        return t('ai-matching-results.page-description.special-allocation-tax-survey.title');
      case 'national-grants':
        return t('ai-matching-results.page-description.national-grants.title');
      case 'prefectural-grants':
        return t('ai-matching-results.page-description.prefectural-grants.title');
    }
  })();

  const skillDescription = (() => {
    switch (mode.category) {
      case 'special-allocation-tax-survey':
        return t('ai-matching-results.page-description.special-allocation-tax-survey.body');
      case 'national-grants':
        return t('ai-matching-results.page-description.national-grants.body');
      case 'prefectural-grants':
        return t('ai-matching-results.page-description.prefectural-grants.body');
    }
  })();

  const candidates = (matchData?.documents ?? []).map(toMatchingCandidate);
  const viewStatus = toCardViewStatus(matchData?.status, isMatchLoading);
  const judgements = Object.fromEntries(candidates.map((c) => [c.candidateId, c.judgement]));

  // 前後ナビゲーションはテーブル表示中の事業 ID 一覧（年度・絞り込み適用済み・表示順）で行う。
  const currentIndex = projectIds.indexOf(projectId);
  const previousProjectId = currentIndex > 0 ? projectIds[currentIndex - 1] : undefined;
  const nextProjectId =
    currentIndex >= 0 && currentIndex < projectIds.length - 1
      ? projectIds[currentIndex + 1]
      : undefined;

  const navigateTo = (nextProjectIdValue: string | undefined) => {
    if (!nextProjectIdValue) return;
    setOpen({
      isOpen: true,
      mode: {
        type: 'ai-matching-project-detail',
        category: mode.category,
        fundingSourceId,
        documentId,
        projectId: nextProjectIdValue,
        year: mode.year,
        projectIds,
      },
    });
  };

  const accordionItems = [
    {
      key: 'basic-info',
      trigger: <Text variant="label">{t('common.sections.basic-info')}</Text>,
      content: (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Text variant="description" className="text-text-secondary">
              {t('ai-matching-results.project-detail.accordion.accounting-type-label')}
            </Text>
            <Text variant="body">{project.accountingType}</Text>
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="description" className="text-text-secondary">
              {t('common.fields.expenditure-category')}
            </Text>
            <Text variant="body">{project.expenditureItem}</Text>
          </div>
        </div>
      ),
    },
    {
      key: 'budget-content',
      trigger: <Text variant="label">{t('project.sections.budget-content')}</Text>,
      content: isDetailLoading ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <LabeledText label={t('project.fields.budget-amount')}>
            <Text viewMode="number" unit="thousand">
              {detail?.budget_amount}
            </Text>
          </LabeledText>
          <LabeledText label={t('project.fields.project-description')} preserveWhitespace>
            {detail?.project_description}
          </LabeledText>
          <LabeledText label={t('project.fields.budget-description')} preserveWhitespace>
            {detail?.budget_description}
          </LabeledText>
        </div>
      ),
    },
    {
      key: 'management',
      trigger: <Text variant="label">{t('project.sections.management')}</Text>,
      content: (
        <ProjectTagsManagementSection
          key={projectId}
          projectId={projectId}
          detail={detail}
          isLoading={isDetailLoading}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col">
      <SideMenuHeader
        title={project.projectName}
        onClose={requestClose}
        additionalMenuItems={
          <>
            <Button
              asChild
              variant="outline"
              size="icon"
              aria-label={t('ai-matching-results.project-detail.open-detail')}
              tooltip={{ content: t('ai-matching-results.project-detail.open-detail') }}
            >
              <Link
                to="/projects/$projectId"
                params={{ projectId }}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </Link>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigateTo(previousProjectId)}
              disabled={!previousProjectId}
              aria-label={t('ai-matching-results.project-detail.go-to-previous')}
              tooltip={{
                content: t('ai-matching-results.project-detail.go-to-previous'),
                enabled: !!previousProjectId,
              }}
            >
              <ArrowUpIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigateTo(nextProjectId)}
              disabled={!nextProjectId}
              aria-label={t('ai-matching-results.project-detail.go-to-next')}
              tooltip={{
                content: t('ai-matching-results.project-detail.go-to-next'),
                enabled: !!nextProjectId,
              }}
            >
              <ArrowDownIcon className="h-4 w-4" />
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-4 px-4 py-4">
        <Text variant="description" className="text-text-secondary">
          {`${project.organization} ｜ ${project.expenditureItem}`}
        </Text>

        <Banner
          type="info"
          title={t('ai-matching-results.project-detail.judgement-banner')}
          showTypeIcon
        />

        <MatchingSkillCard
          skillTitle={skillTitle}
          description={skillDescription}
          candidates={candidates}
          viewStatus={viewStatus}
          highlightedFundingSourceId={fundingSourceId}
          judgements={judgements}
          onJudgementChange={updateJudgement}
        />

        <Accordion type="multiple" defaultActiveKey={['basic-info']} items={accordionItems} />
      </div>
    </div>
  );
};
